/**
 * @fileoverview User Management page.
 * @description The backend's Sales user directory (GET /users) as a filterable table matching the
 * spec's "Search and Filters" section (search, role, reporting manager, state, status) and "User
 * List" columns (Name with Employee ID beneath, Email, Role, Department, Reporting Manager, State,
 * Phone, Date of Joining, Status, Actions). The backend narrows the directory to the viewer's
 * authority (every Sales user for a Super Admin or Sales Head, the downline for a Sales Manager)
 * and does the filtering: the search is debounced, and the Role, Reporting Manager and State
 * choices come from GET /users/filter-options. The KPI cards come from GET /users/summary and
 * ignore the filters; each also shows its active and inactive counts. Clicking a row opens View
 * User (GET /users/{userId}). What a row's three-dot menu and the View dialog offer comes from the
 * user's `allowedActions`, which the backend works out for the caller: Edit (PATCH
 * /users/{userId}), Deactivate and Reactivate (POST /users/{userId}/deactivate and /reactivate,
 * each with its own dialog) and Resend invitation (the user's `pendingInvitationId`). Revoke
 * invitation is offered the same way (`REVOKE_INVITATION`), so a pending user outside the viewer's
 * downline never shows it; "Add User" stays for viewers who hold `users:invite` (AddUserModal),
 * which sends a real invitation (POST /identity/invitations). The dialogs that carry a user are
 * mounted only while open, so each starts from a clean form.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 * @author Anshul Rokde
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'

import { useCurrentSession } from '@/features/auth'
import {
  PageLoadError,
  TableEmptyState,
  TableFooter,
  TableSearchInput,
  TableSkeleton,
} from '@/features/sales'
import {
  AddUserModal,
  RevokeInvitationModal,
  useUserInvitationRowActions,
} from '@/features/user-invitations'
import {
  DeactivateUserModal,
  EditUserModal,
  ReactivateUserModal,
  USER_TABLE_COLUMN_COUNT,
  UserDirectoryFilters,
  UserDirectoryTable,
  UserKpiStrip,
  UserManagementFlash,
  useStateChoices,
  useUserDialogs,
  useUserDirectoryFilters,
  useUserFilterOptions,
  useUserManagementNotices,
  useUsers,
  useUsersSummary,
  ViewUserModal,
} from '@/features/users'
import Button from '@/shared/components/atoms/Button'
import KpiStripSkeleton from '@/shared/components/molecules/KpiStripSkeleton'
import PageHeader from '@/shared/components/molecules/PageHeader'

import type { DirectoryUser, UserRowHandlers } from '@/features/users'

const EMPTY_USERS: DirectoryUser[] = []
const EMPTY_STATES: { stateId: string; name: string }[] = []

interface UserManagementDialogsProps {
  dialogs: ReturnType<typeof useUserDialogs>
  notices: ReturnType<typeof useUserManagementNotices>
  invitations: ReturnType<typeof useUserInvitationRowActions>
  addFormOpen: boolean
  onCloseAddForm: () => void
  onResendViewedUser: (target: { pendingInvitationId: string | null; displayName: string }) => void
}

/**
 * @description Renders every dialog the page can open: Add User, Revoke Invitation, View, and the
 * Edit, Deactivate and Reactivate dialogs, which are mounted only while open so each use starts
 * from a clean form.
 * @param {UserManagementDialogsProps} props - Each dialog's state and handlers.
 */
function UserManagementDialogs({
  dialogs,
  notices,
  invitations,
  addFormOpen,
  onCloseAddForm,
  onResendViewedUser,
}: UserManagementDialogsProps) {
  const stateChoices = useStateChoices()

  return (
    <>
      <AddUserModal
        open={addFormOpen}
        onClose={onCloseAddForm}
        states={stateChoices.data ?? EMPTY_STATES}
        onCreated={notices.onUserCreated}
      />

      <RevokeInvitationModal
        invitation={invitations.revokingInvitation}
        onClose={invitations.closeRevoke}
        onRevoked={notices.onInvitationRevoked}
      />

      <ViewUserModal
        userId={dialogs.viewingUserId}
        onClose={dialogs.closeView}
        onEdit={dialogs.editViewedUser}
        onDeactivate={dialogs.deactivateViewedUser}
        onReactivate={dialogs.reactivateViewedUser}
        onResendInvitation={onResendViewedUser}
      />

      {/* The dialogs below are mounted only while open, so every use starts from a clean form. */}
      {dialogs.editingUserId && (
        <EditUserModal
          userId={dialogs.editingUserId}
          onClose={dialogs.closeEdit}
          onUpdated={notices.onUserUpdated}
        />
      )}

      {dialogs.deactivatingUserId && (
        <DeactivateUserModal
          userId={dialogs.deactivatingUserId}
          onClose={dialogs.closeDeactivate}
          onDeactivated={notices.onUserDeactivated}
        />
      )}

      {dialogs.reactivatingUserId && (
        <ReactivateUserModal
          userId={dialogs.reactivatingUserId}
          onClose={dialogs.closeReactivate}
          onReactivated={notices.onReactivated}
        />
      )}
    </>
  )
}

/**
 * @component
 */
function UserManagementPage() {
  const { t } = useTranslation()
  const canInvite = useCurrentSession().data?.privileges?.includes('users:invite') ?? false
  const [searchParams] = useSearchParams()

  const filters = useUserDirectoryFilters()
  const usersQuery = useUsers(filters.queryFilters)
  const summaryQuery = useUsersSummary()
  const filterOptions = useUserFilterOptions('DIRECTORY', true)
  // Deep-linkable: Sales Team's "Manage User" action navigates here with ?userId=... so the
  // profile opens immediately, rather than landing on the plain unfiltered list.
  const dialogs = useUserDialogs(searchParams.get('userId') ?? '')
  const notices = useUserManagementNotices()
  const invitations = useUserInvitationRowActions({
    onStart: notices.clearActionError,
    onResent: notices.onInvitationResent,
    onFailed: notices.showActionError,
    onNotFound: notices.onInvitationNotFound,
  })
  const [addFormOpen, setAddFormOpen] = useState(false)
  const rows = usersQuery.data ?? EMPTY_USERS

  /**
   * @description Opens the Add User dialog.
   */
  const openAddForm = () => {
    setAddFormOpen(true)
  }

  /**
   * @description Closes the Add User dialog.
   */
  const closeAddForm = () => {
    setAddFormOpen(false)
  }

  /**
   * @description Re-sends the pending invitation of a row's user.
   * @param {DirectoryUser} target - The row's user.
   */
  const resendRowInvitation = (target: DirectoryUser) => {
    if (target.pendingInvitationId !== null) {
      invitations.resend(target.pendingInvitationId, target.displayName)
    }
  }

  /**
   * @description Re-sends the viewed user's pending invitation and closes the profile.
   * @param {{ pendingInvitationId: string | null; displayName: string }} target - The viewed user.
   */
  const resendViewedUserInvitation = (target: {
    pendingInvitationId: string | null
    displayName: string
  }) => {
    if (target.pendingInvitationId !== null) {
      invitations.resend(target.pendingInvitationId, target.displayName)
    }
    dialogs.closeView()
  }

  const rowHandlers: UserRowHandlers = {
    onView: dialogs.openView,
    onEdit: dialogs.openEdit,
    onReactivate: dialogs.openReactivate,
    onDeactivate: dialogs.openDeactivate,
    onResendInvitation: resendRowInvitation,
    onRevokeInvitation: invitations.startRevoke,
  }

  // Add User opens a dialog that is only mounted once the directory has loaded, so it is offered
  // only then: not while loading, nor after a failed first load.
  const header = (
    <PageHeader
      title={t('nav.userManagement')}
      description={t('sales.userManagement.description')}
      actions={
        canInvite && usersQuery.data !== undefined ? (
          <div className="w-fit">
            <Button
              type="button"
              size="md"
              onClick={openAddForm}
              data-testid="user-management-add-user-button"
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <path d="M12 5.5v13M5.5 12h13" />
              </svg>
              {t('sales.userManagement.addUser')}
            </Button>
          </div>
        ) : undefined
      }
    />
  )

  if (usersQuery.isPending) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <KpiStripSkeleton />
        <TableSkeleton columns={USER_TABLE_COLUMN_COUNT} />
      </div>
    )
  }

  // A failed background refetch keeps the rows already loaded; only a first load that failed (no
  // data to show) replaces the page, so the filters, dialogs and flash survive a transient error.
  if (usersQuery.isError && usersQuery.data === undefined) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <PageLoadError
          message={t('sales.userManagement.loadError')}
          testId="user-management-load-error"
        />
      </div>
    )
  }

  const summary = summaryQuery.data

  return (
    // Grows to the viewport's height, and the users card with it, so a short list doesn't leave a
    // blank band below the card.
    <div className="flex grow flex-col gap-4">
      {header}

      {notices.flash && (
        <UserManagementFlash message={notices.flash} onDismiss={notices.dismissFlash} />
      )}

      {usersQuery.isError && (
        <p
          role="alert"
          className="text-[13px] text-danger"
          data-testid="user-management-refresh-error"
        >
          {t('sales.userManagement.loadError')}
        </p>
      )}

      {notices.actionError && (
        <p
          role="alert"
          className="text-[13px] text-danger"
          data-testid="user-management-action-error"
        >
          {notices.actionError}
        </p>
      )}

      {summaryQuery.isPending ? <KpiStripSkeleton /> : <UserKpiStrip summary={summary} />}

      <section className="flex grow flex-col rounded-card border border-divider bg-white">
        <h2 className="sr-only" data-testid="user-management-section-title">
          {t('sales.userManagement.sectionTitle')}
        </h2>
        <UserDirectoryFilters
          filters={filters}
          choices={filterOptions}
          search={
            <TableSearchInput
              value={filters.values.query}
              onChange={filters.setQuery}
              placeholder={t('sales.userManagement.searchPlaceholder')}
              data-testid="user-management-search-input"
            />
          }
        />
        <UserDirectoryTable
          users={rows}
          handlers={rowHandlers}
          emptyState={
            <TableEmptyState
              message={t('sales.userManagement.noResults')}
              clearLabel={t('sales.userManagement.filters.clear')}
              onClear={filters.clear}
            />
          }
        />
        <TableFooter
          line={t('sales.userManagement.footerLine', {
            shown: rows.length,
            total: summary?.totalUsers ?? rows.length,
          })}
        />
      </section>

      <UserManagementDialogs
        dialogs={dialogs}
        notices={notices}
        invitations={invitations}
        addFormOpen={addFormOpen}
        onCloseAddForm={closeAddForm}
        onResendViewedUser={resendViewedUserInvitation}
      />
    </div>
  )
}

export default UserManagementPage
